import { z } from 'zod';
/**
 * Plan catalogue contract (E-SF02) — the shape US-Shop's shelf + detail render,
 * and the shape US-Enrollment-BFF's `/bff/plans*` routes emit.
 *
 * ── WHY THESE LIVE IN THE CONTRACT ─────────────────────────────────────────
 * They are the one edge between the SPA and the BFF (ADR-0002 §4). The BFF uses
 * them as its `response` schema (so the boot-time response-schema guard walks
 * them and the handler validates its own output); US-Shop imports them for typed
 * fetchers. Neither declares `tenantId` or any credential — the BFF derives
 * tenant from Host server-side and never echoes it (AC1c).
 *
 * ── MONEY ON THE WIRE ──────────────────────────────────────────────────────
 * Upstream Pricing serialises money as decimal STRINGS (BigInt micro-cents are
 * not JSON-safe), and this contract carries them through as strings. `rateCents`
 * / `etfCents` are ¢ strings; the price-points values are ¢/kWh strings rounded
 * to the nearest 0.1¢ (§25.475(g)(2)). No floats, no client-side money math.
 */
export const PlanRateType = z.enum(['FIXED', 'INDEXED', 'VARIABLE']);
/** One card on the shelf — the list projection. */
export const PlanSummarySchema = z.object({
    tariffId: z.string().min(1),
    planName: z.string().min(1),
    supplierName: z.string().min(1),
    tdsp: z.string().min(1),
    fixedOrIndexed: PlanRateType,
    /** REP energy charge only, ¢/kWh string. NOT the average price — see price-points. */
    rateCentsPerKwh: z.string().min(1),
    termMonths: z.number().int().nonnegative(),
    /** Early-termination fee, ¢ string. */
    etfCents: z.string().min(1),
    /** Minimum renewable content %, 0..100. */
    recPctMin: z.number().min(0).max(100),
    /**
     * EFL PDF URL as published upstream. ⚠️ For the seeded catalogue this is a
     * placeholder (`example.com` / empty) — the shelf must NOT present it as a
     * live document (STUBS.md; real EFLs are E-SF06 / legal).
     */
    eflPdfUrl: z.string(),
});
/**
 * The §25.475(g)(2) average price at the three EFL reference usages. Each value
 * is a ¢/kWh string rounded to the nearest 0.1¢.
 *
 * ⚠️ THE THREE ARE **NOT** GUARANTEED TO DECLINE. This comment used to assert
 * that they "DECLINE monotonically (500 > 1000 > 2000) — the signature of a
 * compliant Texas EFL". That is false, and upstream Pricing says so in the same
 * words at `us-price-points-route.ts`: average price at usage `u` is
 * `perKwhTotal + netFixed / u`, so the curve declines only while `netFixed` is
 * positive. A monthly credit larger than the fixed charges INVERTS it, and a
 * threshold credit is non-monotonic by construction. Nothing in this repo may
 * order, validate or explain the shelf on the assumption of a falling curve.
 *
 * ⚠️ Declared HERE, above the list, because the shelf list embeds it (see
 * `PlanListEntrySchema`). Declaring it below and reaching back with `z.lazy`
 * would work for type inference and then quietly defeat the BFF's boot-time
 * response-schema walk, which is the guard that keeps forbidden fields off every
 * public response.
 */
export const PricePointsResponseSchema = z.object({
    tariffId: z.string().min(1),
    customerClass: z.literal('RESIDENTIAL'),
    unit: z.literal('cents_per_kwh'),
    scale: z.number().int(),
    kwh500: z.string().min(1),
    kwh1000: z.string().min(1),
    kwh2000: z.string().min(1),
});
/**
 * WHY a plan carries no §25.475 average price — when the absence is a DECISION
 * upstream rather than a failure.
 *
 * ── THE DISTINCTION THIS EXISTS TO MAKE ────────────────────────────────────
 * Pricing REFUSES to publish an average price for a plan whose advertised terms
 * its EFL calculation does not model — `US_TX_EFL_UNMODELLED_CREDIT`, and the
 * counsel-gated `US_TX_EFL_THRESHOLD_CREDIT_PENDING_COUNSEL`. That is a correct,
 * permanent, reasoned refusal under §25.475(c)(1)(A): publishing anyway would
 * OVERSTATE the price (4Change Maxx Saver Value 12 computes 15.15 ¢/kWh at
 * 1,000 kWh against a true 2.65 — 5.7×).
 *
 * Both refusals arrive as HTTP 503, which is indistinguishable from Pricing being
 * down unless the machine `code` is read. Until this field existed the BFF read
 * only the status, so a deliberate refusal reached the customer as a bare 500 and
 * the card rendered "Price unavailable" — the same words it shows for an outage.
 * The customer was told nothing, on a plan an agent had just offered them, and
 * the card looked broken. (Exactly the defect `esiidAlreadyEnrolled` /
 * `esiidNotActive` / `esiidTdspMismatch` were introduced to close on the quote
 * path: an expected upstream refusal flattened into an unexplained fault.)
 *
 * ── ONE REASON, DELIBERATELY ───────────────────────────────────────────────
 * Both upstream codes collapse to `UNMODELLED_PROMOTIONAL_TERM`, following this
 * repo's rule that refusals merge when the remedy is identical and split when it
 * differs (see `PROBLEM_TYPES.offerNoLongerAvailable`). The customer's situation
 * is the same either way — no average price is published for this plan, its real
 * terms are in its EFL — and splitting them would publish which of OUR internal
 * gates is closed, which is operator information on an anonymous public surface.
 *
 * ⚠️ IT IS NOT A CATCH-ALL. `US_TX_TDSP_RATE_CARD_MISSING` is also a 503 and is
 * deliberately NOT mapped here: an unseeded TDU rate card is an operational gap,
 * not a property of the plan, and describing it to a customer as a promotional
 * term would be a fabrication. That one stays an outage and stays retryable.
 *
 * ⚠️ AND IT NAMES NO AMOUNT. Pricing's context carries the matched marketing
 * token and text; neither is on this wire. The value of an unmodelled credit is
 * precisely the thing we have established we cannot compute, so no figure
 * derived from it may be described to a customer.
 */
export const PriceWithheldReasonSchema = z.enum(['UNMODELLED_PROMOTIONAL_TERM']);
/**
 * One card on the shelf, WITH its §25.475 price points folded in
 * (QA-2026-08-05 BLOCKER 2).
 *
 * ── WHY THE LIST CARRIES THE PRICE NOW ─────────────────────────────────────
 * The headline on every card IS the price-points figure — `PlanPriceHeadline` is
 * the only component allowed to render "the price", and it is fed exclusively by
 * this endpoint. So a shelf of ~20 plans issued `GET /bff/plans` plus one
 * `GET /bff/plans/:id/price-points` PER CARD: ~21 requests for one render,
 * against a per-session budget of 30/minute. Three renders and the shopper was
 * 429'd off their own storefront — including off `/bff/session`, so "start
 * again" was capped too.
 *
 * The limiter was doing exactly what it should; the fan-out was the defect. The
 * shelf needs every card's price anyway (it sorts by headline rate), so the data
 * that was always going to be fetched now arrives with the list it belongs to.
 *
 * ── WHY OPTIONAL ───────────────────────────────────────────────────────────
 * A tariff whose price points Pricing cannot serve (404, or an outage on that
 * one read) still appears on the shelf, without them — and the SPA falls back to
 * fetching that ONE plan's points the old way. Failing the whole shelf because a
 * single tariff's EFL data is missing would trade a request-budget bug for an
 * availability bug, and the standalone `/bff/plans/:id/price-points` route
 * remains the source of truth for that card.
 */
export const PlanListEntrySchema = PlanSummarySchema.extend({
    pricePoints: PricePointsResponseSchema.optional(),
    /**
     * Present ONLY when Pricing declined to publish (see `PriceWithheldReason`).
     * Mutually exclusive with `pricePoints`: a refusal produces no figure to carry.
     *
     * ── WHY IT RIDES ON THE LIST AND NOT JUST ON THE 409 ───────────────────
     * A card with neither field falls back to fetching its own points, which is
     * the correct behaviour for a transient omission. A WITHHELD plan is not
     * transient — Pricing will refuse it identically on every request — so
     * without this field all 8 of the live catalogue's 44 withheld tariffs would
     * re-fetch on every shelf render, re-creating a fifth of the fan-out the
     * inline-points change removed (QA-2026-08-05 BLOCKER 2), and burning 8 of a
     * 30/minute session budget to be told "no" eight times.
     */
    priceWithheld: PriceWithheldReasonSchema.optional(),
});
export const PlanListResponseSchema = z.object({
    plans: z.array(PlanListEntrySchema),
});
/** A per-component effective-dated rate line on the detail (additive). */
export const PlanComponentSchema = z.object({
    code: z.string().min(1),
    rateCentsPerKwh: z.string().min(1),
    effectiveFrom: z.string().min(1),
    effectiveTo: z.string().nullable(),
    source: z.string().min(1),
    tdsp: z.string().optional(),
});
export const PlanDetailResponseSchema = PlanSummarySchema.extend({
    /** ⚠️ Placeholder text for the seeded catalogue (STUBS.md). */
    eflText: z.string(),
    tosText: z.string(),
    yracText: z.string(),
    variableFormula: z.string().nullable(),
    effectiveFrom: z.string().min(1),
    effectiveTo: z.string().nullable(),
    components: z.array(PlanComponentSchema),
});
//# sourceMappingURL=catalog.js.map